import {
  BarChart3,
  CalendarDays,
  CreditCard,
  FileText,
  FolderKanban,
  LayoutGrid,
  LogOut,
  Settings,
  Timer,
  UserCog,
  Users,
  X,
} from "lucide-react"
import { NavLink } from "react-router-dom"
import { useAuth } from "../auth/AuthProvider"
import { cx } from "../lib/cx"
import type { Role } from "../types"

const navItems = [
  { label: "Dashboard", icon: LayoutGrid, path: "/", end: true, roles: ["Admin"] as Role[] },
  { label: "Clients", icon: Users, path: "/clients", roles: ["Admin", "Staff"] as Role[] },
  { label: "Projects", icon: FolderKanban, path: "/projects", roles: ["Admin", "Staff"] as Role[] },
  { label: "Time Tracking", icon: Timer, path: "/time", roles: ["Admin", "Staff", "Contractor"] as Role[] },
  { label: "Billing", icon: CreditCard, path: "/billing", roles: ["Admin"] as Role[] },
  { label: "Reports", icon: BarChart3, path: "/reports", roles: ["Admin"] as Role[] },
  { label: "Tasks", icon: CalendarDays, path: "/tasks", roles: ["Admin", "Staff", "Contractor"] as Role[] },
  { label: "Users", icon: UserCog, path: "/users", roles: ["Admin"] as Role[] },
  { label: "Sales", icon: FileText, path: "/sales", roles: ["Admin"] as Role[] },
  { label: "Settings", icon: Settings, path: "/settings", roles: ["Admin", "Staff", "Contractor"] as Role[] },
]

type SidebarProps = {
  onClose?: () => void
}

const Sidebar = ({ onClose }: SidebarProps) => {
  const { user, logout } = useAuth()
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
    : "AD"

  const handleNavClick = () => {
    if (onClose) {
      onClose()
    }
  }

  const visibleNavItems = navItems.filter((item) =>
    user?.role ? item.roles.includes(user.role) : false,
  )

  return (
    <div className="flex h-full w-20 flex-col items-center gap-4 rounded-[28px] bg-white/90 py-6 shadow-soft lg:w-20">
      {/* Mobile Header */}
      {onClose && (
        <div className="flex w-full items-center justify-between border-b border-line px-4 pb-4 lg:hidden">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-moss text-white shadow-soft">
            <span className="text-sm font-semibold">BP</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone transition hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Desktop Logo */}
      {!onClose && (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-moss text-white shadow-soft">
          <span className="text-lg font-semibold">BP</span>
        </div>
      )}

      {/* Navigation Items */}
      <div className="flex flex-1 flex-col items-center gap-3">
        {visibleNavItems.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.label}
              to={item.path}
              end={item.end}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cx(
                  "group relative flex h-11 w-11 items-center justify-center rounded-2xl border border-transparent text-moss transition-all duration-200",
                  isActive
                    ? "bg-moss text-white shadow-soft scale-105"
                    : "hover:bg-pearl hover:text-moss hover:scale-105",
                )
              }
              title={item.label}
            >
              <Icon className="h-5 w-5 transition-transform group-hover:scale-110" />
              {/* Mobile Label Tooltip */}
              {onClose && (
                <span className="pointer-events-none absolute left-full ml-2 rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                  {item.label}
                </span>
              )}
            </NavLink>
          )
        })}
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={() => {
            logout()
            if (onClose) onClose()
          }}
          className="flex h-11 w-11 items-center justify-center rounded-2xl text-stone transition-all duration-200 hover:bg-pearl hover:text-moss hover:scale-105"
          title="Sign out"
        >
          <LogOut className="h-5 w-5 transition-transform hover:scale-110" />
        </button>
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600 transition-all duration-200 hover:scale-105">
          {initials}
        </div>
      </div>
    </div>
  )
}

export default Sidebar
